import { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find, isEqual } from 'lodash-es'
import { useDebounce } from 'use-debounce'
import { Calculate } from '@mui/icons-material'
import { Box, Stack, TextField, Typography } from '@mui/material'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { useQuery } from '@tanstack/react-query'
import { LabelledValueRow, Section } from '@ui2/structure'
import { Info } from '@ui/feedback'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { useResultsStore, useStiffeningStore, useSystemManagerStore } from '@editorStores'
import {
  useBlockAction,
  useElementLabel,
  useElementTypes,
  useGuidToElement,
  useResultsSelection,
} from '@editorHooks'
import { buildErrorMessage } from 'src/constants/errors'
import { postAnchorCheckPreview } from 'src/state/api/generated/anchor-check-preview/anchor-check-preview'
import { useGetDesignForcesPerSupport } from 'src/state/api/generated/design-forces-per-support/design-forces-per-support'
import {
  ConnectorSelection,
  isSameConnectorSelection,
  PRIORITY_LIST_SELECTION,
} from '../../connectorSelection'
import useAnchorSides from '../../hooks/useAnchorSides'
import useCornerConnectionAdditionalLoadProposalState from '../../hooks/useCornerConnectionAdditionalLoadProposalState'
import useDesignFormNavigationGuard from '../../hooks/useDesignFormNavigationGuard'
import useSaveAnchorManualLoads from '../../hooks/useSaveAnchorManualLoads'
import { connectorSelectionOfRow } from '../../hooks/useSaveAnchorManualLoads/utils'
import { selectResultsNavigationForSupportTarget } from '../../utils'
import ConnectorSelectionSelect from '../ConnectorSelectionSelect'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import type { Results2RelationLink } from '../Results2DetailPane/components/Results2RelationLinksRow'
import { RESULTS2_DESIGN_FORM_BLOCK_ACTION } from '../Results2DetailPane/constants'
import { Results2ListItem } from '../Results2ElementList'
import Results2PaneHeader from '../Results2PaneHeader'
import AnchorDesignForceCheckTable from './components/AnchorDesignForceCheckTable'
import AnchorLoadResults from './components/AnchorLoadResults'
import AnchorManualLoadsSection from './components/AnchorManualLoadsSection'
import AnchorSaveRow from './components/AnchorSaveRow'
import {
  AnchorLoadCategory,
  AnchorManualLoadDraft,
  draftsToManualLoads,
  formatMeters,
  hasInvalidManualLoadDraft,
  manualLoadDraftSum,
  PredictedAnchorCheck,
} from './utils'

interface Props {
  supportGuid: string
  selectedItem?: Results2ListItem
  /** Disables edits while a list autosave (e.g. the comment column) is dirty or in flight. */
  disableForm?: boolean
}

interface DraftState {
  /** Saved rows/comment/Zuganker the draft was initialized from — used to detect refetched data. */
  baseDrafts: AnchorManualLoadDraft[]
  baseComment: string
  baseSelection: ConnectorSelection
  drafts: AnchorManualLoadDraft[]
  comment: string
  selection: ConnectorSelection
}

const relationLabelForTab: Partial<Record<string, string>> = {
  columns: 'zur Stütze',
  beams: 'zum Unterzug',
  purlins: 'zur Pfette',
}

/**
 * Detail pane of one tensile anchor in the Results2 "Anker" tab: general info, the combined
 * design-force & check table (Wind | Erdbeben), editable manual loads with the inline
 * corner-connection helper, and the load breakdown known from the tensile-anchor export.
 */
const Results2AnchorDetailPane = ({
  supportGuid,
  selectedItem,
  disableForm = false,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const getElementLabel = useElementLabel()
  const guidToElement = useGuidToElement()
  const elementTypes = useElementTypes()
  const guardDesignFormNav = useDesignFormNavigationGuard()

  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const anchorChecks = useResultsStore(state => state.anchorChecks)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const connections = useSystemManagerStore(state => state.connections)

  const {
    selectResultsAnchor: selectAnchor,
    selectResultsShearAnchor: selectShearAnchor,
    selectResultsStiffeningSegment: selectStiffeningSegment,
    selectResultsPosition: selectPosition,
  } = useResultsSelection()

  const { saveAnchorEdits, isSaving, isComputingAnchorChecks } = useSaveAnchorManualLoads()
  const { cornerConnectionState, abortHelper } = useCornerConnectionAdditionalLoadProposalState()

  const generalSection = useResultsSectionOpen('results2.anchor.general')
  const designCheckSection = useResultsSectionOpen('results2.anchor.design-check')
  const manualLoadsSection = useResultsSectionOpen('results2.anchor.manual-loads')
  const loadsSection = useResultsSectionOpen('results2.anchor.loads')

  const row = useMemo(
    () => find(anchorInterventionTableData ?? [], { support_guid: supportGuid }),
    [anchorInterventionTableData, supportGuid],
  )
  const check = useMemo(
    () => find(anchorChecks ?? [], { element_guid: supportGuid }),
    [anchorChecks, supportGuid],
  )

  const designForcesQuery = useGetDesignForcesPerSupport(projectId as string, {
    query: {
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Anker-Einwirkungen'),
          { variant: 'error' },
        ),
    },
  })
  const designForces = designForcesQuery.data?.[supportGuid]

  // ----- manual loads / comment draft (reinitialized whenever the saved row changes) -----

  const savedDrafts = useMemo<AnchorManualLoadDraft[]>(
    () =>
      (row?.manual_loads ?? []).map(load => ({ source: load.source, formula: load.load_formula })),
    [row],
  )
  const savedComment = row?.comment ?? ''
  const savedSelection = useMemo(
    () => (row ? connectorSelectionOfRow(row) : PRIORITY_LIST_SELECTION),
    [row],
  )

  const [draftState, setDraftState] = useState<DraftState | null>(null)

  if (draftState === null) {
    setDraftState({
      baseDrafts: savedDrafts,
      baseComment: savedComment,
      baseSelection: savedSelection,
      drafts: savedDrafts,
      comment: savedComment,
      selection: savedSelection,
    })
  } else if (
    !isEqual(draftState.baseDrafts, savedDrafts) ||
    draftState.baseComment !== savedComment ||
    !isSameConnectorSelection(draftState.baseSelection, savedSelection)
  ) {
    // Refetched rows re-anchor the base, but a field the user has edited locally keeps its draft
    // (e.g. an unsaved manual load must survive the echo of the list's comment autosave).
    const draftsEditedLocally = !isEqual(draftState.drafts, draftState.baseDrafts)
    const commentEditedLocally = draftState.comment !== draftState.baseComment
    const selectionEditedLocally = !isSameConnectorSelection(
      draftState.selection,
      draftState.baseSelection,
    )
    setDraftState({
      baseDrafts: savedDrafts,
      baseComment: savedComment,
      baseSelection: savedSelection,
      drafts: draftsEditedLocally ? draftState.drafts : savedDrafts,
      comment: commentEditedLocally ? draftState.comment : savedComment,
      selection: selectionEditedLocally ? draftState.selection : savedSelection,
    })
  }
  const drafts = draftState?.drafts ?? savedDrafts
  const comment = draftState?.comment ?? savedComment
  const selection = draftState?.selection ?? savedSelection

  // Mirror of `draftState` that `updateDraft` advances synchronously, so two edits in the same
  // tick build on each other (the corner-connection helper writes the load row and the comment
  // back to back) instead of both starting from this render's snapshot.
  const draftStateRef = useRef<DraftState | null>(null)
  draftStateRef.current = draftState

  const updateDraft = useCallback(
    (
      update: (
        current: DraftState,
      ) => Partial<Pick<DraftState, 'drafts' | 'comment' | 'selection'>>,
    ) => {
      const current = draftStateRef.current
      if (!current) return
      const next = { ...current, ...update(current) }
      draftStateRef.current = next
      setDraftState(next)
    },
    [],
  )

  const setDrafts = useCallback(
    (next: AnchorManualLoadDraft[]) =>
      updateDraft(current => ({ drafts: next, comment: current.comment })),
    [updateDraft],
  )
  const setComment = useCallback(
    (next: string) => updateDraft(current => ({ drafts: current.drafts, comment: next })),
    [updateDraft],
  )
  const setSelection = useCallback(
    (next: ConnectorSelection) => updateDraft(() => ({ selection: next })),
    [updateDraft],
  )
  const appendComment = useCallback(
    (line: string) =>
      updateDraft(current => ({
        drafts: current.drafts,
        comment: current.comment ? `${current.comment} ${line}` : line,
      })),
    [updateDraft],
  )

  const manualLoadsDirty = !isEqual(drafts, savedDrafts)
  const commentDirty = comment !== savedComment
  const selectionDirty = !isSameConnectorSelection(selection, savedSelection)
  const hasInvalidDraft = hasInvalidManualLoadDraft(drafts)

  /**
   * Persists the manual loads and the comment of the detail pane — like the other Results2 detail
   * panes, nothing here saves itself. Only changed manual loads are sent (and recompute the anchor
   * checks) — rebuilding unchanged ones could differ from the persisted values and recompute anyway.
   */
  const cornerActive = cornerConnectionState.type !== 'idle'

  const saveChanges = useCallback(() => {
    const current = draftStateRef.current
    if (!current || cornerActive || hasInvalidManualLoadDraft(current.drafts)) return
    const loadsEdited = !isEqual(current.drafts, current.baseDrafts)
    const selectionEdited = !isSameConnectorSelection(current.selection, current.baseSelection)
    saveAnchorEdits({
      [supportGuid]: {
        ...(loadsEdited ? { manualLoads: draftsToManualLoads(current.drafts) } : {}),
        comment: current.comment,
        ...(selectionEdited ? { connectorSelection: current.selection } : {}),
      },
    })
  }, [cornerActive, saveAnchorEdits, supportGuid])

  /** Drops every local edit: the manual-load draft, the comment and the Zuganker. */
  const revertChanges = useCallback(() => {
    if (cornerActive) abortHelper()
    const current = draftStateRef.current
    if (!current) return
    const next = {
      ...current,
      drafts: current.baseDrafts,
      comment: current.baseComment,
      selection: current.baseSelection,
    }
    draftStateRef.current = next
    setDraftState(next)
  }, [abortHelper, cornerActive])

  const savedManualSum = manualLoadDraftSum(savedDrafts)
  // The check table previews the draft only while every row is valid; otherwise it keeps showing
  // the saved state instead of a sum that silently skips the broken rows.
  const isPreviewingLoads = manualLoadsDirty && !hasInvalidDraft
  const previewManualSum = isPreviewingLoads ? manualLoadDraftSum(drafts) : savedManualSum
  const isPreviewing = isPreviewingLoads || selectionDirty

  // Live prediction of the anchor the backend would pick for the previewed forces — computed by
  // the backend itself (`anchor-check-preview` reuses the anchor-check pipeline without
  // persisting), debounced so typing doesn't fire a request per keystroke. Falling back to the
  // saved sum skips the debounce, so "Rückgängig" or an invalid row switch back at once. Supports
  // without connection candidates (columns/rips) return an empty list → no prediction.
  const [debouncedManualSum] = useDebounce(previewManualSum, 400)
  const previewQuerySum = previewManualSum === savedManualSum ? savedManualSum : debouncedManualSum
  const previewQuery = useQuery({
    // Always the draft Zuganker: a preview without it would show the prioritization's choice
    // for an anchor whose Zuganker is fixed.
    queryKey: [
      'anchor-check-preview',
      projectId,
      supportGuid,
      previewQuerySum,
      selection.policy,
      selection.connectorId,
    ],
    queryFn: ({ signal }) =>
      postAnchorCheckPreview(
        projectId as string,
        {
          support_guid: supportGuid,
          manual_load_sum: previewQuerySum,
          connectorSelectionPolicy: selection.policy,
          userDefinedConnectorId: selection.connectorId,
        },
        signal,
      ),
    enabled: !!projectId && !!designForces,
    keepPreviousData: true,
    // The preview also depends on the connectors' resistances, the prioritization and the design
    // forces, none of which is in the key, and the client caches queries forever by default. So a
    // preview lives only as long as the pane: a newly opened pane always asks the backend again
    // instead of showing a prediction made with since-edited Zugverbinder (until it arrives, the
    // table shows the stored check).
    staleTime: 0,
    cacheTime: 0,
    onError: (error: unknown) =>
      enqueueSnackbar(
        buildErrorMessage(error as AxiosError, 'Fehler beim Ermitteln der Anker-Vorschau'),
        { variant: 'error' },
      ),
  })
  const predictions = useMemo(():
    | Partial<Record<AnchorLoadCategory, PredictedAnchorCheck>>
    | undefined => {
    const previewChecks = previewQuery.data
    if (!previewChecks || previewChecks.length === 0) return undefined
    const result: Partial<Record<AnchorLoadCategory, PredictedAnchorCheck>> = {}
    for (const previewCheck of previewChecks) {
      const category = previewCheck.load_category
      if (category !== 'Wind' && category !== 'EarthQuake') continue
      const connection = previewCheck.used_anchor as unknown as Connection
      result[category] = {
        connection,
        resistance:
          previewCheck.design_resistance ??
          (category === 'Wind' ? connection.r_d_wind : connection.r_d_earthquake),
        utilization: previewCheck.utilization,
      }
    }
    return Object.keys(result).length > 0 ? result : undefined
  }, [previewQuery.data])

  // Reusing the Bemessung block action makes the existing guards (tab switch, list selection, esc,
  // reload) ask for the save first.
  useBlockAction(
    RESULTS2_DESIGN_FORM_BLOCK_ACTION,
    manualLoadsDirty || commentDirty || selectionDirty ? 'provider' : undefined,
  )

  // ----- context: side, segment geometry, relation links -----

  const anchorSides = useAnchorSides()
  const side = anchorSides[supportGuid]

  const segment = useMemo(
    () => (row?.segment_guid ? find(stiffeningIntervals, { guid: row.segment_guid }) : undefined),
    [row, stiffeningIntervals],
  )

  const wallLength = useMemo(() => {
    if (!row) return undefined
    const element = guidToElement[row.element_guid] as ShapeObject | undefined
    const points = element?.shape?.points
    if (!points || points.length < 2) return undefined
    return points[0].distanceTo(points[1])
  }, [guidToElement, row])

  // Stiffening intervals are absolute meters along the wall (same interpretation as the scene's
  // SimpleStiffeningMesh) — no scaling by the wall length.
  const segmentStart = segment ? parseFloat(segment.interval.lower) : undefined
  const segmentEnd = segment ? parseFloat(segment.interval.upper) : undefined
  const segmentLength =
    segmentStart !== undefined && segmentEnd !== undefined ? segmentEnd - segmentStart : undefined

  const siblingAnchorGuid = useMemo(() => {
    if (!row?.segment_guid) return undefined
    return (anchorInterventionTableData ?? []).find(
      other => other.segment_guid === row.segment_guid && other.support_guid !== row.support_guid,
    )?.support_guid
  }, [anchorInterventionTableData, row])

  const relationLinks = useMemo((): Results2RelationLink[] => {
    if (!row) return []

    if (row.segment_guid) {
      const links: Results2RelationLink[] = []
      if (siblingAnchorGuid) {
        links.push({
          label: side === 'links' ? 'zum rechten Anker' : 'zum linken Anker',
          tooltip: 'Anderen Anker des Segments auswählen',
          onClick: () => {
            if (guardDesignFormNav()) return
            selectAnchor(siblingAnchorGuid)
          },
        })
      }
      links.push({
        label: 'zur Wandscheibe',
        tooltip: 'Wandscheibe auswählen',
        onClick: () => {
          if (guardDesignFormNav()) return
          if (row.segment_guid) selectStiffeningSegment(row.segment_guid)
        },
      })
      links.push({
        // The shear anchor is chosen per stiffening segment, so this anchor's
        // segment is what identifies it.
        label: 'zum Schubanker',
        tooltip: 'Schubanker-Nachweis öffnen',
        onClick: () => {
          if (guardDesignFormNav()) return
          if (row.segment_guid) selectShearAnchor(row.segment_guid)
        },
      })
      links.push({
        label: 'zur Wand',
        tooltip: 'Wand auswählen',
        onClick: () => {
          if (guardDesignFormNav()) return
          selectPosition(row.element_guid, 'walls', 'wall-assemblies')
        },
      })
      return links
    }

    const navigation = selectResultsNavigationForSupportTarget({
      guid: row.element_guid,
      elementType: elementTypes[row.element_guid],
      standardRipChecks,
    })
    if (navigation.kind !== 'position') return []
    const label =
      navigation.tab === 'walls'
        ? navigation.wallSubTab === 'standard-rips'
          ? 'zur Std-Rippe'
          : 'zur Rippe'
        : relationLabelForTab[navigation.tab] ?? 'zum Element'
    return [
      {
        label,
        tooltip: 'Element auswählen',
        onClick: () => {
          if (guardDesignFormNav()) return
          selectPosition(navigation.guid, navigation.tab, navigation.wallSubTab)
        },
      },
    ]
  }, [
    elementTypes,
    guardDesignFormNav,
    row,
    selectAnchor,
    selectPosition,
    selectShearAnchor,
    selectStiffeningSegment,
    siblingAnchorGuid,
    side,
    standardRipChecks,
  ])

  // ----- render -----

  if (!row) {
    return (
      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Info icon={<Calculate />}>Daten noch nicht verfügbar</Info>
      </Box>
    )
  }

  // The list label uses the compact side suffix ("li"/"re"); the pane title spells it out.
  const baseLabel = row.segment_guid
    ? getElementLabel(row.segment_guid) || row.segment_guid
    : getElementLabel(row.element_guid) || row.element_guid
  const title = side ? `${baseLabel} · ${side}` : selectedItem?.label ?? baseLabel
  const subtitle = `Zuganker · ${getElementLabel(row.element_guid) || row.element_guid}`

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[{ id: 'design', label: 'Bemessung' }]}
          value="design"
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>

      <Results2PaneHeader title={title} subtitle={subtitle} relationLinks={relationLinks} />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
        <Stack spacing={2.5} sx={{ py: 1.5 }}>
          <TextField
            size="small"
            label="Kommentar"
            value={comment}
            onChange={event => setComment(event.target.value)}
            disabled={cornerActive || disableForm}
            fullWidth
            data-cy="anchor-comment-field"
          />

          <Section title="Allgemein" collapsible hideBorderBottom {...generalSection}>
            <LabelledValueRow
              items={[
                {
                  label: row.segment_guid ? 'Wand' : 'Element',
                  value: getElementLabel(row.element_guid) || '-',
                },
                { label: 'Position', value: side ? `Anker ${side}` : '-' },
                {
                  label: 'Wandlänge',
                  value: wallLength === undefined ? undefined : formatMeters(wallLength),
                },
                {
                  label: 'Segmentlänge',
                  value: segmentLength === undefined ? undefined : formatMeters(segmentLength),
                },
                {
                  label: 'Segmenthöhe',
                  value: segment ? formatMeters(segment.effective_height) : undefined,
                },
                {
                  label: 'Segment im Wandverlauf',
                  value:
                    segmentStart === undefined || segmentEnd === undefined
                      ? undefined
                      : `${formatMeters(segmentStart)} – ${formatMeters(segmentEnd)}`,
                },
              ]}
            />
          </Section>

          <Section
            title="Resultierende Design-Zuglast & Nachweis"
            collapsible
            hideBorderBottom
            {...designCheckSection}
          >
            {row.segment_guid ? (
              <Box sx={{ pt: 0.5, pb: 1 }}>
                <ConnectorSelectionSelect
                  value={selection}
                  onChange={next => next && setSelection(next)}
                  connections={connections}
                  label="Zuganker"
                  dataCy="anchor-connector-selection"
                  disabled={cornerActive || disableForm}
                />
              </Box>
            ) : undefined}
            {check ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                Gewählt: <b>{check.used_anchor.name}</b>
                {savedSelection.policy === 'UserDefined' &&
                check.used_anchor.guid === savedSelection.connectorId
                  ? ' (manuell festgelegt)'
                  : ''}
              </Typography>
            ) : undefined}
            <AnchorDesignForceCheckTable
              designForces={designForces}
              check={check}
              savedManualSum={savedManualSum}
              draftManualSum={previewManualSum}
              isDirty={isPreviewing}
              predictions={predictions}
            />
          </Section>

          <Section title="Manuelle Lasten" collapsible hideBorderBottom {...manualLoadsSection}>
            <AnchorManualLoadsSection
              supportGuid={supportGuid}
              drafts={drafts}
              onChange={setDrafts}
              onAppendComment={appendComment}
              disabled={disableForm}
            />
          </Section>

          <Section
            title="Einwirkungen (be- und entlastend)"
            collapsible
            hideBorderBottom
            {...loadsSection}
          >
            <AnchorLoadResults
              designForces={designForces}
              isLoading={designForcesQuery.isLoading}
            />
          </Section>
        </Stack>
      </Box>

      <AnchorSaveRow
        isSaving={isSaving || isComputingAnchorChecks}
        manualLoadsDirty={manualLoadsDirty}
        commentDirty={commentDirty}
        connectorSelectionDirty={selectionDirty}
        hasInvalidDraft={hasInvalidDraft}
        disabled={disableForm}
        saveBlocked={cornerActive}
        onSave={saveChanges}
        onRevert={revertChanges}
      />
    </Box>
  )
}

export default Results2AnchorDetailPane
